import {
    ResourceCard,
    ResourceCardTextField,
    ResourceCardTitle
} from "./ResourceCard";

import { Assessment } from "../../API";
import { Box } from "@mui/material";
import CircleIcon from '@mui/icons-material/Circle';
import { ExpandableCarousel } from "./image/ExpandableCarousel";
import { ExternalUserInfo } from "../../models";
import { FieldDisplay } from "./FieldDisplay";
import { ImageEntity } from "../../lib/dashboard/types/ImageEntity";
import { ItemLabel } from "./ItemLabel";
import { TagsControl } from "./TagsControl";
import { format } from "date-fns";
import { useAssessments } from "./contexts/AssessmentsContextProvider";
import { useMemo } from "react";
import { useResolveImageEntity } from "../../lib/dashboard/hooks/useResolveImageEntity";
import { useTags } from "./contexts/TagsContextProvider";

type SuccessfulAssessmentCardProps = {
    assessment: Assessment;
    images: Array<ImageEntity>;
    inFocus: boolean;
};

const LABEL_TITLE = "All Clear";
const LABEL_COLOR = "success";
const LABEL_ICON = CircleIcon;
const BG_COLOR = {
    r: 76,
    g: 175,
    b: 80
};

export const SuccessfulAssessmentCard = (props: SuccessfulAssessmentCardProps) => {
    const {
        assessment,
        images,
        inFocus,
    } = props;

    const { resolvedImageEntities } = useResolveImageEntity(images);
    const { tags, createTag } = useTags();
    const { onEditAssessmentSubmit } = useAssessments();

    const cardTitle = useMemo<string>(() => {
        if (assessment.createdTime) {
            return `Assessment on ${format(new Date(assessment.createdTime!), "MMMM d, yyyy")}`;
        }
        return `Assessment on ${format(new Date(assessment.createdAt!), "MMMM d, yyyy")}`;
    }, [assessment]);

    const submittedAtString = useMemo<string>(() => {
        if (assessment.createdTime) {
            return format(new Date(assessment.createdTime!), "yyyy-MM-dd h:mm a");
        }
        return format(new Date(assessment.createdAt!), "yyyy-MM-dd h:mm a");
    }, [assessment]);

    return (
        <ResourceCard
            inFocus={inFocus}
            imageCarousel={<ExpandableCarousel images={resolvedImageEntities} />}
            borderColor="success.main"
            bgColor={BG_COLOR}
            content={
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    flex={3}
                    justifyContent="space-around"
                >
                    <Box display="flex" flexDirection="column">
                        <Box display="flex" justifyContent="space-between" alignItems="center" position="relative">
                            <ResourceCardTitle>{cardTitle}</ResourceCardTitle>
                        </Box>
                        <ItemLabel
                            text={LABEL_TITLE}
                            color={LABEL_COLOR}
                            Icon={LABEL_ICON}
                        />
                    </Box>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                        <FieldDisplay
                            label="Description"
                            data={assessment.description || "-"}
                            onEditSubmit={onEditAssessmentSubmit ? (data: string) => {
                                onEditAssessmentSubmit(assessment.id, {
                                    ...assessment,
                                    description: data
                                });
                            } : undefined}
                            editView={({ data, setData }) => {
                                return (
                                    <ResourceCardTextField
                                        value={data}
                                        onChange={(e) => setData(e.target.value)}
                                    />
                                );
                            }}
                        />
                        <FieldDisplay
                            label="Submitted by"
                            data={{
                                firstName: assessment.submittedBy?.firstName || "",
                                lastName: assessment.submittedBy?.lastName || ""
                            }}
                            readView={`${assessment.submittedBy?.firstName} ${assessment.submittedBy?.lastName}` || "-"}
                        />
                        <FieldDisplay
                            label="Reported"
                            data={submittedAtString}
                        />
                        <TagsControl
                            options={tags}
                            selectedTagIds={assessment.tagIds || []}
                            onTagChange={onEditAssessmentSubmit ? (tagIds: Array<string>) => {
                                onEditAssessmentSubmit(assessment.id, {
                                    ...assessment,
                                    tagIds
                                });
                            } : undefined}
                            createTag={createTag}
                        />
                    </Box>
                </Box>
            }
        />
    );
};
